import {useLocation, useNavigate} from "react-router-dom";
import {Box, CloseButton, Container, Flex, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {TtaSave, ttaSelector, useGetTtaByPickupAndDeliveryQuery, useGetTtaQuery} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useEffect, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {getTransportPatch, TransportFormValues} from "../../components/form/TransportForm/TransportForm";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {toFormDefaultsFromTransport} from "../TransportFormPage/TransportFormPage";
import {
  TicketForm,
  TicketFormDefaultValues,
  TicketFormOptions,
  TicketFormValues
} from "../../components/form/TicketForm/TicketForm";
import {useTtaMutation} from "../../hooks/useTtaMutation";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";

type Props = {
  id?: string;
  pickupId?: string
  deliveryId?: string
  database?: string
  copy?: boolean;
  options?: TicketFormOptions,
  formDefaults?: TicketFormDefaultValues
}

export const TicketFormPageRoute = () => {
  const {state} = useLocation();

  const id = state?.id;
  const pickupId = state?.pickupId
  const deliveryId = state?.deliveryId
  const database = state?.database

  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  const options = state?.options;

  return (
    <TicketFormPage
      id={id}
      pickupId={pickupId}
      deliveryId={deliveryId}
      database={database}
      copy={copy}
      formDefaults={formDefaults}
      options={options}
    />
  );
};

export const TicketFormPage = (props: Props) => {
  const {
    id,
    pickupId,
    deliveryId,
    database,
    copy = false,
    formDefaults,
    options
  } = props;

  const {regionId, appCategory} = useApp()

  const navigate = useNavigate();

  const {t} = useTranslation(["common"]);

  const [access, setAccess] = useState(false);
  const [loading, setLoading] = useState(false)

  const selectTicket = useMemo(() => {
    return ttaSelector()
  }, []);

  const idQueryArg = useMemo(() => {
    return access ? (id ? {id: id, database: database} : skipToken) : skipToken
  }, [access, id, database])

  const {idTicket, isIdFetching} = useGetTtaQuery(idQueryArg, {
    selectFromResult: (result) => ({
      idTicket: selectTicket(result),
      isIdFetching: result.isFetching
    })
  });

  const pickupDeliveryQueryArg = useMemo(() => {
    if (access && !id && pickupId && deliveryId) {
      return {
        pickupId: pickupId,
        deliveryId: deliveryId,
        database: database
      }
    } else {
      return skipToken
    }
  }, [access, id, pickupId, deliveryId, database])

  const {pdTicket, isPDFetching} = useGetTtaByPickupAndDeliveryQuery(pickupDeliveryQueryArg, {
    selectFromResult: (result) => ({
      pdTicket: selectTicket(result),
      isPDFetching: result.isFetching
    })
  });

  useEffect(() => {
    setLoading(isIdFetching || isPDFetching)
  }, [isIdFetching, isPDFetching]);

  const currTicket = idTicket ?? pdTicket

  const _formDefaults: TicketFormDefaultValues = useMemo(() => {
    return {
      pickup: formDefaults?.pickup ? formDefaults.pickup : toFormDefaultsFromTransport(currTicket?.pickupTransport),
      delivery: formDefaults?.delivery ? formDefaults.delivery : toFormDefaultsFromTransport(currTicket?.deliveryTransport),
      manualLoadedMileage: formDefaults?.manualLoadedMileage ?? currTicket?.manualLoadedMileage,
      manualUnloadedMileage: formDefaults?.manualUnloadedMileage ?? currTicket?.manualUnloadedMileage,
    }
  }, [currTicket, formDefaults]);

  const isEdit = useMemo(() => {
    return typeof currTicket !== 'undefined' && !copy
  }, [currTicket, copy])

  const title: string = useMemo(() => {
    if (isEdit) {
      return t("page.entity.transporttransportassoc.form.title.edit", "Edit Ticket", {ns: "common"});
    } else {
      return t("page.entity.transporttransportassoc.form.title.new", "New Ticket", {ns: "common"});
    }
  }, [isEdit, t]);

  const {
    saveTta,
    isSaving,
  } = useTtaMutation()

  const onFormSubmit = useCallback((formValues: TicketFormValues) => {
    const patch: TtaSave = {
      pickupTransport: getTransportPatch(formValues.pickup, regionId),
      deliveryTransport: getTransportPatch(formValues.delivery, regionId),
      manualLoadedMileage: formValues.manualLoadedMileage,
      manualUnloadedMileage: formValues.manualUnloadedMileage,
      regionId: regionId,
    }

    if (isEdit) {
      const tta: TtaSave = {
        ...{
          id: currTicket?.id,
          pickupTransportId: currTicket?.pickupTransportId,
          deliveryTransportId: currTicket?.deliveryTransportId,
          regionId: currTicket.regionId,
        },
        ...patch,
        pickupTransport: {
          ...patch.pickupTransport,
          id: currTicket?.pickupTransport?.id ?? currTicket?.pickupTransportId,
        },
        deliveryTransport: {
          ...patch.deliveryTransport,
          id: currTicket?.deliveryTransport?.id ?? currTicket?.deliveryTransportId,
        },
        category: patch.pickupTransport?.category ?? patch.deliveryTransport?.category
      };
      saveTta(tta, () => navigate(-1))
    } else {
      const tta: TtaSave = {
        ...patch,
        pickupTransportId: patch.pickupTransport?.id ?? pickupId,
        deliveryTransportId: patch.deliveryTransport?.id ?? deliveryId,
        regionId: regionId,
        category: patch.pickupTransport?.category ?? patch.deliveryTransport?.category,
      };
      saveTta(tta, () => navigate(-1))
    }
  }, [regionId, currTicket, isEdit, saveTta, navigate, pickupId, deliveryId]);

  return (
    <ProtectedPage
      permissions={[isEdit ? "transporttransportassoc_edit" : "transporttransportassoc_create"]}
      isLoading={loading}
      onAccess={setAccess}>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="sm">
              <Flex direction="column" h="100%" m="sm" gap="sm">
                {loading && <Loader/>}
                {!loading && (
                  <>
                    <Paper shadow="sm" p="md">
                      <Group position="apart">
                        <Title align="center">
                          {title}
                        </Title>
                        <CloseButton
                          title="Close"
                          size="xl"
                          iconSize={20}
                          onClick={() => navigate(-1)}
                        />
                      </Group>
                    </Paper>
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                      <TicketForm
                        options={{
                          ...options,
                          regionId: options?.regionId ?? currTicket?.regionId ?? regionId,
                          category: options?.category ?? currTicket?.category ?? appCategory,
                          categoryDisabled: (!copy && typeof currTicket !== 'undefined') || options?.categoryDisabled,
                          pickupHasGeofenceEvent: options?.pickupHasGeofenceEvent ?? (!copy && typeof currTicket?.pickupTransport?.geofenceEvent !== 'undefined'),
                          deliveryHasGeofenceEvent: options?.deliveryHasGeofenceEvent ?? (!copy && typeof currTicket?.deliveryTransport?.geofenceEvent !== 'undefined')
                        }}
                        defaultValues={_formDefaults}
                        submitting={isSaving}
                        onSubmit={onFormSubmit}
                      />
                    </Box>
                  </>
                )}
              </Flex>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ProtectedPage>
  );
};

